import { useEffect, useRef, useState } from "react";
import { X, Gift } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/shared/components/ui/card";

interface PostCallReferralPromptProps {
  onClose: () => void;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
}

export function PostCallReferralPrompt({
  onClose,
  guestName = "",
  guestEmail = "",
  guestPhone = ""
}: PostCallReferralPromptProps) {
  const widgetRef = useRef<HTMLDivElement>(null);
  const [showWidget, setShowWidget] = useState(false);

  // Initialize Referral Rocket widget when user clicks "Refer Now"
  useEffect(() => {
    if (!showWidget) return;

    console.log('[PostCallReferralPrompt] Initializing Referral Rocket widget for campaign h8e60aWA');

    // Set the campaign-id attribute for the business referral campaign
    if (widgetRef.current) {
      widgetRef.current.setAttribute('campaign-id', 'h8e60aWA');
      console.log('[PostCallReferralPrompt] Widget div created with campaign-id:',
        widgetRef.current.getAttribute('campaign-id'));
    }

    // Load Referral Rocket widget script (only if not already loaded)
    const existingScript = document.querySelector('script[src="https://app.referralrocket.io/widget/widgetIndex.js"]');
    if (!existingScript) {
      console.log('[PostCallReferralPrompt] Loading Referral Rocket widget script...');
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.setAttribute('campaign-id', 'h8e60aWA');
      script.defer = true;
      script.src = 'https://app.referralrocket.io/widget/widgetIndex.js';

      script.onload = () => {
        console.log('[PostCallReferralPrompt] Referral Rocket widget script loaded successfully');
      };

      script.onerror = (error) => {
        console.error('[PostCallReferralPrompt] Failed to load Referral Rocket script:', error);
      };

      document.head.appendChild(script);

      return () => {
        try {
          document.head.removeChild(script);
        } catch (e) {
          // Script might already be removed
        }
      };
    } else {
      console.log('[PostCallReferralPrompt] Referral Rocket script already loaded');
    }
  }, [showWidget]);

  if (!showWidget) {
    // Show initial "Refer Now" prompt
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
        <Card className="max-w-md w-full bg-white shadow-2xl my-4">
          <CardHeader className="relative pb-4">
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Centered title */}
            <CardTitle className="text-xl text-gray-900 text-center">
              Get a $25 Amazon Gift Card!
            </CardTitle>
          </CardHeader>

          <CardContent className="space-y-6">
            <div className="text-center space-y-2">
              <p className="text-gray-600">
                Know a business that could benefit from video calls with their customers?
              </p>
              <p>
                <strong className="text-black">Refer them and earn a $25 Amazon Gift Card!</strong>
              </p>
            </div>

            {/* Info box */}
            <div className="border border-gray-600 rounded-lg p-4">
              <h4 className="font-semibold text-gray-800 text-sm mb-2">
                How it works:
              </h4>
              <ul className="text-xs text-gray-700 space-y-1">
                <li>✓ Share wlkthru with a pro</li>
                <li>✓ They get $100 credit when they sign up</li>
                <li>✓ You get $25 Amazon gift card!</li>
              </ul>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={onClose}
                className="flex-1"
              >
                Maybe Later
              </Button>
              <Button
                onClick={() => setShowWidget(true)}
                className="flex-1 bg-primary hover:bg-primary/90"
              >
                <Gift className="h-4 w-4 mr-2" />
                Refer Now
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show Referral Rocket widget
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 overflow-y-auto">
      <Card className="w-full h-full sm:h-auto sm:max-w-4xl sm:max-h-[90vh] bg-white shadow-2xl my-2 sm:my-4 flex flex-col">
        <CardHeader className="relative pb-4 flex-shrink-0">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </CardHeader>

        <CardContent className="flex-1 overflow-y-auto pb-6">
          {/* Referral Rocket Widget - Full height on mobile, auto on desktop */}
          <div
            ref={widgetRef}
            className="hype_widget min-h-[500px] sm:min-h-[600px]"
            style={{
              width: '100%',
              height: 'auto'
            }}
          ></div>
        </CardContent>
      </Card>
    </div>
  );
}
